import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  addInspectionActivity,
  getInspectionActivities,
  getInspectionChecklistQueries,
  getInspectionFindings,
  getInspectionList,
  getLoggedInUser,
  getUsersList,
  respondToInspectionChecklistQuery,
  saveInspectionFindingManagementResponse,
  startInspectionReview,
  updateInspectionAssignment,
} from "../../helpers/backend_helper";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import SummaryCards from "../../components/Common/SummaryCards";
import WorkflowResponseDrawer from "../../components/Inspections/WorkflowResponseDrawer";
import "./InspectionReviewList.scss";

const startOfToday = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const dueState = (value) => {
  if (!value) return "none";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "none";
  const days = Math.ceil(
    (new Date(date.getFullYear(), date.getMonth(), date.getDate()) -
      startOfToday()) /
      86400000
  );
  if (days < 0) return "overdue";
  if (days <= 7) return "week";
  return "upcoming";
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return includeTime
    ? date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const formatDateLong = (value, includeTime = false) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const dateText = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return includeTime
    ? `${dateText}\n${date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : dateText;
};

const reviewTone = (status = "") => {
  if (/reviewer complete|awaiting approval/i.test(status)) return "review";
  if (/complete|closed/i.test(status)) return "completed";
  if (/awaiting quer(?:y|ies)/i.test(status)) return "query";
  if (/in review/i.test(status)) return "review";
  if (/progress|review/i.test(status)) return "progress";
  if (/cancel/i.test(status)) return "cancelled";
  return "new";
};

const isInProgressStatus = (status = "") => /in[\s-]?progress/i.test(status);

const reviewProgress = (review) => {
  const percentage = Number(review.percentage);
  if (Number.isFinite(percentage))
    return Math.max(0, Math.min(100, percentage));
  return reviewTone(review.inspectionStatus?.name) === "completed" ? 100 : 0;
};

const reviewRisk = (review) => {
  const level = String(review.overallRiskLevel || "")
    .trim()
    .toLowerCase();
  if (level === "high") return "High";
  if (level === "medium") return "Medium";
  if (level === "low") return "Low";
  return "Not Rated";
};

const primaryActionForReview = (review = {}) => {
  const status = review.inspectionStatus?.name || "";
  const hasActiveFindingResponse = (review.reviewWorkflowTasks || []).some(
    (task) =>
      task.taskType === "FindingResponse" &&
      /active/i.test(task.workflowStatus || "")
  );
  if (hasActiveFindingResponse) return "Respond to Finding";
  const hasActiveChecklistTask = (review.reviewWorkflowTasks || []).some(
    (task) =>
      task.taskType !== "FinalSignOff" &&
      /active/i.test(task.workflowStatus || "")
  );
  if (/awaiting (quer(?:y|ies)|response)/i.test(status)) {
    // Open queries block submission, not completion of the remaining checklist work.
    return hasActiveChecklistTask || reviewProgress(review) < 100
      ? "Resume Review"
      : "View Queries";
  }
  if (/reviewer complete|awaiting approval/i.test(status))
    return "Review Findings";
  if (/completed/i.test(status)) return "Open Review";
  if (/cancelled/i.test(status)) return "View Review";
  if (isInProgressStatus(status)) return "Resume Review";
  return "Start Review";
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

const workflowTasksFor = (review = {}) => review.reviewWorkflowTasks || [];

const taskAssigneeName = (task = {}) =>
  task.assignedUser?.fullName || task.assignedUserName || "Unassigned";

const uniqueNames = (tasks = []) => [
  ...new Set(
    tasks.map(taskAssigneeName).filter((name) => name && name !== "Unassigned")
  ),
];

const currentWorkflowTasks = (review = {}) =>
  workflowTasksFor(review).filter((task) =>
    /active/i.test(task.workflowStatus || "")
  );

const currentAssigneeNames = (review = {}) => {
  const names = uniqueNames(currentWorkflowTasks(review));
  if (names.length) return names;
  return workflowTasksFor(review).length
    ? []
    : [review.assignedUser?.fullName || "Unassigned"];
};

const displayNames = (names = []) => {
  if (!names.length) return "Unassigned";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
};

const workflowStageLabel = (tasks = []) => {
  if (!tasks.length) return "No action pending";
  if (tasks.every((task) => task.taskType === "FinalSignOff"))
    return "Final sign-off";
  if (tasks.every((task) => /ChecklistSignOff/i.test(task.taskType || "")))
    return "Checklist sign-off";
  if (tasks.every((task) => /Checklist/i.test(task.taskType || "")))
    return "Checklist review";
  if (tasks.every((task) => task.taskType === "FindingResponse"))
    return "Finding response";
  return "Workflow action";
};

const pendingWorkflowTasks = (review = {}) =>
  workflowTasksFor(review).filter(
    (task) => !/^(Completed|Cancelled)$/i.test(task.workflowStatus || "")
  );

const workflowTaskGroup = (task = {}) => {
  if (task.taskType === "FinalSignOff")
    return { id: "final-sign-off", label: "Final sign-off" };
  if (/^Checklist/i.test(task.taskType || ""))
    return { id: "checklists", label: "Checklist work" };
  if (task.taskType === "FindingResponse")
    return { id: "findings", label: "Finding responses" };
  if (["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task.taskType))
    return { id: "queries", label: "Query responses" };
  return { id: "other", label: "Other workflow work" };
};

const groupedPendingWorkflowTasks = (review = {}) => {
  const groups = new Map();
  pendingWorkflowTasks(review).forEach((task) => {
    const group = workflowTaskGroup(task);
    if (!groups.has(group.id)) groups.set(group.id, { ...group, tasks: [] });
    groups.get(group.id).tasks.push(task);
  });
  return [...groups.values()];
};

const workflowTaskLabel = (task = {}) => {
  if (task.taskType === "FinalSignOff")
    return task.taskName || "Final engagement sign-off";
  if (/^Checklist/i.test(task.taskType || ""))
    return `Checklist ${task.checklistId || "—"} · ${
      /ChecklistSignOff/i.test(task.taskType || "") ? "Sign-off" : "Review"
    }`;
  return task.taskName || workflowStageLabel([task]);
};

const PeopleAvatarStack = ({ names = [], label }) => {
  const visibleNames = names.slice(0, 3);
  if (!visibleNames.length) return null;
  return (
    <span
      className="engagement-review-avatar-stack"
      title={names.join(", ")}
      aria-label={label || names.join(", ")}
    >
      {visibleNames.map((name) => (
        <span key={name} className="engagement-review-avatar-stack__avatar">
          {initials(name)}
        </span>
      ))}
      {names.length > visibleNames.length && (
        <span className="engagement-review-avatar-stack__more">
          +{names.length - visibleNames.length}
        </span>
      )}
    </span>
  );
};

const CurrentWorkflowAssignee = ({ review }) => {
  const activeTasks = currentWorkflowTasks(review);
  const hasWorkflow = workflowTasksFor(review).length > 0;
  const names = activeTasks.length
    ? uniqueNames(activeTasks)
    : hasWorkflow
    ? []
    : currentAssigneeNames(review);
  const isUnassigned =
    !names.length || (names.length === 1 && names[0] === "Unassigned");
  return (
    <div className="engagement-review-assignee">
      {!isUnassigned && (
        <PeopleAvatarStack
          names={names}
          label={`Current action owner: ${names.join(", ")}`}
        />
      )}
      <div className="engagement-review-assignee__details">
        <strong>
          {activeTasks.length
            ? displayNames(names)
            : hasWorkflow
            ? "No action pending"
            : displayNames(names)}
        </strong>
        <small>
          {activeTasks.length
            ? `${workflowStageLabel(activeTasks)}${
                activeTasks[0]?.assignedRole
                  ? ` · ${activeTasks[0].assignedRole}`
                  : ""
              }`
            : isUnassigned
            ? "Not assigned"
            : hasWorkflow
            ? "All workflow tasks are complete"
            : `For: ${review.inspectionType?.name || "Review"}`}
        </small>
      </div>
    </div>
  );
};

const reviewAssignmentDefaults = (review) => ({
  owner:
    review.reviewAssignment?.owner ||
    review.ownerUser?.fullName ||
    "ABC Firm Admin",
  primaryReviewer:
    review.reviewAssignment?.primaryReviewer ||
    review.assignedUser?.fullName ||
    "ABC Firm Admin",
  additionalReviewers: review.reviewAssignment?.additionalReviewers || [],
  approver:
    review.reviewAssignment?.approver ||
    review.engagementDetail?.engagementPartner?.fullName ||
    "John Smith",
  observer:
    review.reviewAssignment?.observer ||
    review.engagementDetail?.observer?.fullName ||
    "",
});

const ReviewAssignmentDrawer = ({ review, users, onClose, onSave }) => {
  const [assignment, setAssignment] = useState(() =>
    reviewAssignmentDefaults(review)
  );
  const [assignedTo, setAssignedTo] = useState(() =>
    String(review.assignedTo || review.assignedUser?.id || "")
  );
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [notes, setNotes] = useState("");
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [teamMember, setTeamMember] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignment(reviewAssignmentDefaults(review));
    setAssignedTo(String(review.assignedTo || review.assignedUser?.id || ""));
    setReason("");
    setOtherReason("");
    setNotes("");
    setNotifyMembers(true);
    setTeamMember("");
    setConfirmOpen(false);
  }, [review]);

  const initialAssignment = reviewAssignmentDefaults(review);
  const assigneeChanged =
    Number(assignedTo) !== Number(review.assignedTo || review.assignedUser?.id);
  const hasChanged =
    JSON.stringify(assignment) !== JSON.stringify(initialAssignment) ||
    assigneeChanged;
  const members = [
    ...new Set(
      [
        initialAssignment.owner,
        initialAssignment.primaryReviewer,
        initialAssignment.approver,
        "Sarah Johnson",
        "Michael Brown",
        "David Lee",
        "Emily Davis",
        "James Wilson",
      ].filter(Boolean)
    ),
  ];
  const canSave = Boolean(
    assignedTo &&
      assignment.owner &&
      assignment.primaryReviewer &&
      hasChanged &&
      reason &&
      (reason !== "Other" || otherReason.trim())
  );
  const isInProgress = isInProgressStatus(review.inspectionStatus?.name || "");
  const reviewerChanged =
    assignment.primaryReviewer !== initialAssignment.primaryReviewer ||
    assigneeChanged;

  const updateAssignment = (field, value) =>
    setAssignment((current) => ({ ...current, [field]: value }));

  const addAdditionalReviewer = () => {
    if (teamMember && !assignment.additionalReviewers.includes(teamMember)) {
      updateAssignment("additionalReviewers", [
        ...assignment.additionalReviewers,
        teamMember,
      ]);
    }
    setTeamMember("");
  };

  const removeAdditionalReviewer = (member) =>
    updateAssignment(
      "additionalReviewers",
      assignment.additionalReviewers.filter((item) => item !== member)
    );

  const persistAssignment = async () => {
    setSaving(true);
    try {
      await onSave(
        { ...assignment, assignedTo: Number(assignedTo) },
        {
          reason: reason === "Other" ? otherReason.trim() : reason,
          notes: notes.trim(),
          notifyMembers,
        }
      );
    } finally {
      setSaving(false);
    }
  };

  const saveAssignment = () => {
    if (!canSave) return;
    if (isInProgress && reviewerChanged) {
      setConfirmOpen(true);
      return;
    }
    persistAssignment();
  };

  const confirmReviewerChange = () => {
    setConfirmOpen(false);
    persistAssignment();
  };
  const roleRows = [
    ["Review Owner", initialAssignment.owner, "Engagement Manager"],
    ["Primary Reviewer", initialAssignment.primaryReviewer, "Senior Reviewer"],
    ["Approver", initialAssignment.approver, "Engagement Partner"],
    ["Observer", initialAssignment.observer || "Not assigned", "Observer"],
  ];

  return (
    <>
      <div className="review-assignment-drawer">
        <header className="review-assignment-drawer__header">
          <div>
            <h2>Review Assignment</h2>
            <p>
              Manage responsibility for this review without changing the
              engagement team.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close review assignment"
            onClick={onClose}
          >
            <i className="bx bx-x" />
          </button>
        </header>
        <div className="review-assignment-drawer__content">
          <section className="review-assignment-summary">
            <strong>
              {review.inspectionType?.name || "Review type unavailable"}
            </strong>
            <span>Review Job ID: {review.inspectionNo || "—"}</span>
            <span>Engagement: {review.engagementDetail?.name || "—"}</span>
            <b
              className={`engagement-review-status engagement-review-status--${reviewTone(
                review.inspectionStatus?.name
              )}`}
            >
              {review.inspectionStatus?.name === "Awaiting Approval" ? "Awaiting Sign-off" : review.inspectionStatus?.name || "New"}
            </b>
          </section>

          <section className="review-assignment-section">
            <h3>Current Assignment</h3>
            <div className="review-assignment-current">
              {roleRows.map(([role, user, source]) => (
                <article key={role}>
                  <span>{initials(user)}</span>
                  <div>
                    <b>{user}</b>
                    <small>{role}</small>
                  </div>
                  <em>{source}</em>
                </article>
              ))}
            </div>
          </section>

          <section className="review-assignment-section">
            <h3>Update Assignment</h3>
            <label className="review-assignment-field">
              <span>
                Assigned To <b>*</b>
              </span>
              <select
                value={assignedTo}
                onChange={(event) => setAssignedTo(event.target.value)}
              >
                <option value="">Select assignee</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.fullName}
                  </option>
                ))}
              </select>
              <small>Only this user can start or resume the review.</small>
            </label>
            <label className="review-assignment-field">
              <span>
                Review Owner <b>*</b>
              </span>
              <select
                value={assignment.owner}
                onChange={(event) =>
                  updateAssignment("owner", event.target.value)
                }
              >
                {members.map((member) => (
                  <option key={member}>{member}</option>
                ))}
              </select>
              <small>
                Responsible for coordinating and monitoring this review. Default
                from: Engagement Manager
              </small>
            </label>
            <label className="review-assignment-field">
              <span>
                Primary Reviewer <b>*</b>
              </span>
              <select
                value={assignment.primaryReviewer}
                onChange={(event) =>
                  updateAssignment("primaryReviewer", event.target.value)
                }
              >
                {members.map((member) => (
                  <option key={member}>{member}</option>
                ))}
              </select>
              <small>
                Responsible for completing the review checklist. Default from:
                Senior Reviewer
              </small>
            </label>
            <div className="review-assignment-field">
              <span>Additional Reviewers</span>
              <div className="review-assignment-chips">
                {assignment.additionalReviewers.map((member) => (
                  <button
                    type="button"
                    key={member}
                    onClick={() => removeAdditionalReviewer(member)}
                  >
                    {member}
                    <i className="bx bx-x" />
                  </button>
                ))}
              </div>
              <div className="review-assignment-add">
                <select
                  value={teamMember}
                  onChange={(event) => setTeamMember(event.target.value)}
                >
                  <option value="">Search engagement team members</option>
                  {members
                    .filter(
                      (member) =>
                        !assignment.additionalReviewers.includes(member)
                    )
                    .map((member) => (
                      <option key={member}>{member}</option>
                    ))}
                </select>
                <button type="button" onClick={addAdditionalReviewer}>
                  Add
                </button>
              </div>
              <button
                className="review-assignment-outside"
                type="button"
                onClick={() => setTeamMember("Sarah Johnson")}
              >
                + Add user outside engagement team
              </button>
            </div>
            <label className="review-assignment-field">
              <span>Approver</span>
              <select
                value={assignment.approver}
                onChange={(event) =>
                  updateAssignment("approver", event.target.value)
                }
              >
                <option value="">Select approver</option>
                {members.map((member) => (
                  <option key={member}>{member}</option>
                ))}
              </select>
              <small>Default from: Engagement Partner</small>
            </label>
            <label className="review-assignment-field">
              <span>Observer</span>
              <select
                value={assignment.observer}
                onChange={(event) =>
                  updateAssignment("observer", event.target.value)
                }
              >
                <option value="">Select observer</option>
                {members.map((member) => (
                  <option key={member}>{member}</option>
                ))}
              </select>
              <small>Optional. Default from: Engagement Observer</small>
            </label>
          </section>

          {hasChanged && (
            <>
              <aside className="review-assignment-notice">
                <i className="bx bx-info-circle" />
                <span>
                  This change applies only to this review job. The Engagement
                  Team will not be modified.
                </span>
              </aside>
              <section className="review-assignment-section review-assignment-section--reason">
                <h3>
                  Reason for Change <b>*</b>
                </h3>
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                >
                  <option value="">Select a reason</option>
                  {[
                    "Workload balancing",
                    "Reviewer unavailable",
                    "Conflict of interest",
                    "Required specialist",
                    "Role change",
                    "Management request",
                    "Other",
                  ].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
                {reason === "Other" && (
                  <label className="review-assignment-field">
                    <span>
                      Please specify reason <b>*</b>
                    </span>
                    <input
                      value={otherReason}
                      onChange={(event) => setOtherReason(event.target.value)}
                    />
                  </label>
                )}
                <label className="review-assignment-field">
                  <span>Notes</span>
                  <textarea
                    maxLength="500"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Add any instructions or context for the new reviewer..."
                  />
                  <small>{notes.length}/500 characters</small>
                </label>
                <label className="review-assignment-notify">
                  <input
                    checked={notifyMembers}
                    onChange={(event) => setNotifyMembers(event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    <b>Notify affected team members</b>
                    <small>
                      The previous and newly assigned users will receive a
                      notification.
                    </small>
                  </span>
                </label>
              </section>
            </>
          )}
        </div>
        <footer className="review-assignment-drawer__footer">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="is-primary"
            disabled={!canSave || saving}
            onClick={saveAssignment}
            type="button"
          >
            {saving ? "Saving..." : "Save Assignment"}
          </button>
        </footer>
      </div>
      <Modal centered isOpen={confirmOpen} toggle={() => setConfirmOpen(false)}>
        <ModalBody className="review-assignment-confirm">
          <h3>Change Primary Reviewer?</h3>
          <p>This review is already in progress.</p>
          <p>
            Current reviewer: <b>{initialAssignment.primaryReviewer}</b>
            <br />
            New reviewer: <b>{assignment.primaryReviewer}</b>
          </p>
          <p>
            Existing checklist responses, queries, documents and progress will
            remain unchanged.
          </p>
          <div>
            <button type="button" onClick={() => setConfirmOpen(false)}>
              Cancel
            </button>
            <button
              className="is-primary"
              type="button"
              onClick={confirmReviewerChange}
            >
              Confirm Change
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

const InspectionActionMenu = ({
  review,
  onReviewSummary,
  onStartReview,
  onReviewAssignment,
  onActivityHistory,
  onCancelReview,
  canManage,
  canStart,
}) => {
  const openSummary = () => onReviewSummary(review.id, review);

  const startReview = () => onStartReview(review.id, review);
  const primaryAction = primaryActionForReview(review);

  return (
    <ActionMenu
      label="engagement review"
      triggerClassName="engagement-review-action-trigger"
      contentClassName="engagement-review-action-menu__paper"
      actions={[
        {
          label: "Review Summary",
          icon: <i className="bx bx-file-blank" aria-hidden="true" />,
          onSelect: openSummary,
        },
        {
          label: canStart
            ? primaryAction
            : `${primaryAction} (assigned user only)`,
          icon: <i className="bx bx-play-circle" aria-hidden="true" />,
          className: "is-primary",
          disabled: !canStart,
          onSelect: startReview,
        },
        ...(canManage && !(review.reviewWorkflowTasks || []).length
          ? [
              {
                label: "Review Assignment",
                icon: <i className="bx bx-user-check" aria-hidden="true" />,
                separatorBefore: true,
                onSelect: () => onReviewAssignment(review),
              },
              {
                label: "Change Due Date",
                icon: <i className="bx bx-calendar-edit" aria-hidden="true" />,
                onSelect: () => {},
              },
            ]
          : []),
        {
          label: "Activity History",
          icon: <i className="bx bx-history" aria-hidden="true" />,
          separatorBefore: true,
          onSelect: () => onActivityHistory(review),
        },
        ...(canManage
          ? [
              {
                label: "Cancel Review",
                icon: <i className="bx bx-x-circle" aria-hidden="true" />,
                className: "is-danger",
                onSelect: () => onCancelReview(review),
              },
            ]
          : []),
      ]}
    />
  );
};

const InspectionReviewList = ({ engagementId } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const inspectionPlanId =
    Number(new URLSearchParams(location.search).get("inspectionPlanId")) ||
    null;
  const reviewListOrigin = {
    state: {
      fromPath: `${location.pathname}${location.search}`,
      fromLabel: "Inspection Reviews",
    },
  };
  const [reviews, setReviews] = useState([]);
  const gridRequest = useRef(0);
  useEffect(
    () => () => {
      gridRequest.current += 1;
    },
    []
  );
  const [gridLoading, setGridLoading] = useState(true);
  const [gridError, setGridError] = useState("");
  const [assignmentReview, setAssignmentReview] = useState(null);
  const [historyReview, setHistoryReview] = useState(null);
  const [cancelNoticeReview, setCancelNoticeReview] = useState(null);
  const [reviewActivities, setReviewActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [filters, setFilters] = useState({
    view: engagementId || inspectionPlanId ? "all" : "mine",
    status: "all",
    assigned: "all",
    due: "all",
  });
  const [quickFilter, setQuickFilter] = useState(
    engagementId || inspectionPlanId ? "all" : "mine"
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isEngagementMember, setIsEngagementMember] = useState(false);
  const [workflowResponseDrawer, setWorkflowResponseDrawer] = useState(null);
  const [workflowResponseText, setWorkflowResponseText] = useState("");
  const [savingWorkflowResponse, setSavingWorkflowResponse] = useState(false);
  const [expandedReviewIds, setExpandedReviewIds] = useState(() => new Set());
  const [reviewPagination, setReviewPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [reviewSortModel, setReviewSortModel] = useState([]);
  const handledWorkspaceRequest = useRef("");

  const loadReviews = async () => {
    const requestId = ++gridRequest.current;
    setGridLoading(true);
    setGridError("");
    try {
      const res = await getInspectionList();
      if (requestId !== gridRequest.current) return;
      if (!res.status)
        throw new Error(res.message || "Unable to load reviews.");
      setReviews(
        (res.data || []).filter(
          (item) =>
            (!engagementId ||
              Number(item.engagementId) === Number(engagementId)) &&
            (!inspectionPlanId ||
              Number(item.inspectionPlanId) === inspectionPlanId)
        )
      );
      window.dispatchEvent(new Event("piers:readiness-changed"));
    } catch (error) {
      if (requestId === gridRequest.current)
        setGridError(error.message || "Unable to load reviews.");
    } finally {
      if (requestId === gridRequest.current) setGridLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
    getLoggedInUser().then((account) => {
      const roleName =
        account?.role?.name ||
        account?.roleName ||
        account?.userRole?.name ||
        account?.designation ||
        "";
      setCurrentUser(account);
      setIsEngagementMember(
        Number(account?.roleId) === 4 || /engagement\s+member/i.test(roleName)
      );
    });
    getUsersList()
      .then((response) => {
        if (response?.status)
          setUsers(
            (response.data || []).filter((user) => user.isBlocked !== 1)
          );
      })
      .catch(() => {});
  }, [engagementId, inspectionPlanId]);

  useEffect(() => {
    const request = new URLSearchParams(location.search);
    const inspectionId = Number(request.get("inspectionId"));
    const workflowTaskId = Number(request.get("workflowTaskId"));
    const requestKey =
      inspectionId && (workflowTaskId || request.get("workspace") === "1")
        ? `${inspectionId}:${workflowTaskId || "overview"}`
        : "";
    if (
      !currentUser ||
      !requestKey ||
      !reviews.length ||
      handledWorkspaceRequest.current === requestKey
    )
      return;
    const review = reviews.find((item) => Number(item.id) === inspectionId);
    if (!review) return;
    handledWorkspaceRequest.current = requestKey;
    navigate(
      `/inspectionreview/${review.id}${
        workflowTaskId ? `?workflowTaskId=${workflowTaskId}` : ""
      }`,
      { replace: true }
    );
  }, [location.search, reviews, currentUser, navigate]);

  const assignedToMe = (review) => {
    const currentUserId = currentUser?.id ?? currentUser?.userId;
    if (!currentUserId) return false;

    return (
      Number(review.assignedTo) === Number(currentUserId) ||
      Number(review.assignedUser?.id) === Number(currentUserId) ||
      (review.reviewWorkflowTasks || []).some(
        (task) =>
          Number(task.assignedTo) === Number(currentUserId) &&
          /active/i.test(task.workflowStatus || "")
      )
    );
  };

  const activeWorkflowTaskForCurrentUser = (review) => {
    const currentUserId = currentUser?.id ?? currentUser?.userId;
    if (!currentUserId) return null;
    return (
      (review.reviewWorkflowTasks || []).find(
        (task) =>
          Number(task.assignedTo) === Number(currentUserId) &&
          /active/i.test(task.workflowStatus || "")
      ) || null
    );
  };

  const canOpenWorkflowTask = (task) => {
    const currentUserId = currentUser?.id ?? currentUser?.userId;
    return (
      Boolean(currentUserId) &&
      Number(task?.assignedTo) === Number(currentUserId) &&
      /active/i.test(task?.workflowStatus || "")
    );
  };

  const workflowTaskActionLabel = (task) => {
    if (["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task?.taskType)) return "Respond to Query";
    if (task?.taskType === "FindingResponse") return "Respond to Finding";
    if (task?.taskType === "FinalSignOff") return "Open Final Sign-off";
    return /ChecklistSignOff/i.test(task?.taskType || "")
      ? "Open Sign-off"
      : "Open Review";
  };

  const assigneeOptions = useMemo(
    () => [
      ...new Set(
        reviews
          .flatMap(currentAssigneeNames)
          .filter((name) => name !== "Unassigned")
      ),
    ],
    [reviews]
  );

  const visibleReviews = useMemo(
    () =>
      reviews.filter((review) => {
        const status = review.inspectionStatus?.name || "New";
        const due = dueState(review.dueDate);
        const quickMatches =
          quickFilter === "all" ||
          (quickFilter === "mine" && assignedToMe(review)) ||
          (quickFilter === "assigned" && assignedToMe(review)) ||
          (quickFilter === "overdue" && due === "overdue") ||
          (quickFilter === "week" && due === "week") ||
          (quickFilter === "new" && reviewTone(status) === "new") ||
          (quickFilter === "progress" && reviewTone(status) === "progress") ||
          (quickFilter === "queries" &&
            /awaiting quer(?:y|ies)/i.test(status)) ||
          (quickFilter === "review" && /in review/i.test(status)) ||
          (quickFilter === "completed" && reviewTone(status) === "completed");

        return (
          quickMatches &&
          (filters.view === "all" || assignedToMe(review)) &&
          (filters.status === "all" || status === filters.status) &&
          (filters.assigned === "all" ||
            currentAssigneeNames(review).includes(filters.assigned) ||
            (filters.assigned === "mine" && assignedToMe(review))) &&
          (filters.due === "all" || due === filters.due)
        );
      }),
    [reviews, filters, quickFilter, currentUser]
  );

  const sortedVisibleReviews = useMemo(() => {
    const sort = reviewSortModel[0];
    if (!sort?.field || !sort.sort) return visibleReviews;

    const valueFor = (review) => {
      if (sort.field === "reviewJob") return review.inspectionNo || "";
      if (sort.field === "reviewType") return review.inspectionType?.name || "";
      if (sort.field === "inspectionCycle") return review.inspectionCycleName || "";
      if (sort.field === "engagement") return review.engagementDetail?.name || "";
      if (sort.field === "template")
        return (
          review.reviewTemplate?.name ||
          review.reviewTemplateVersion?.name ||
          ""
        );
      if (sort.field === "assignedTo")
        return displayNames(currentAssigneeNames(review));
      if (sort.field === "dueDate")
        return new Date(review.dueDate || 0).getTime();
      if (sort.field === "statusProgress")
        return review.inspectionStatus?.name || "";
      if (sort.field === "engagementRisk") return reviewRisk(review);
      return "";
    };
    return [...visibleReviews].sort((left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: "base",
            });
      return sort.sort === "desc" ? -comparison : comparison;
    });
  }, [visibleReviews, reviewSortModel]);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      reviewPagination.pageSize === -1 ? 0 : Math.ceil(sortedVisibleReviews.length / reviewPagination.pageSize) - 1
    );
    if (reviewPagination.page > lastPage)
      setReviewPagination((current) => ({ ...current, page: lastPage }));
  }, [
    sortedVisibleReviews.length,
    reviewPagination.page,
    reviewPagination.pageSize,
  ]);

  const paginatedReviews = useMemo(
    () =>
      reviewPagination.pageSize === -1 ? sortedVisibleReviews : sortedVisibleReviews.slice(
        reviewPagination.page * reviewPagination.pageSize,
        (reviewPagination.page + 1) * reviewPagination.pageSize
      ),
    [sortedVisibleReviews, reviewPagination]
  );

  const reviewTreeRows = useMemo(
    () =>
      paginatedReviews.flatMap((review) => {
        const groups = groupedPendingWorkflowTasks(review);
        const parent = {
          ...review,
          gridRowId: `review-${review.id}`,
          __treeRowType: "review",
          __hasPendingWorkflow: groups.length > 0,
        };
        if (!expandedReviewIds.has(review.id) || !groups.length)
          return [parent];
        return [
          parent,
          ...groups.flatMap((group) => [
            {
              ...review,
              gridRowId: `review-${review.id}-group-${group.id}`,
              __treeRowType: "workflow-group",
              __workflowGroup: group,
            },
            ...group.tasks.map((task) => ({
              ...review,
              gridRowId: `review-${review.id}-task-${task.id}`,
              __treeRowType: "workflow-task",
              __workflowTask: task,
            })),
          ]),
        ];
      }),
    [paginatedReviews, expandedReviewIds]
  );

  const toggleReviewExpansion = (reviewId) =>
    setExpandedReviewIds((current) => {
      const next = new Set(current);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });

  const counts = useMemo(
    () => ({
      total: reviews.length,
      new: reviews.filter(
        (review) => reviewTone(review.inspectionStatus?.name) === "new"
      ).length,
      progress: reviews.filter(
        (review) => reviewTone(review.inspectionStatus?.name) === "progress"
      ).length,
      completed: reviews.filter(
        (review) => reviewTone(review.inspectionStatus?.name) === "completed"
      ).length,
      overdue: reviews.filter(
        (review) => dueState(review.dueDate) === "overdue"
      ).length,
      week: reviews.filter((review) => dueState(review.dueDate) === "week")
        .length,
      awaitingQueries: reviews.filter((review) =>
        /awaiting quer(?:y|ies)/i.test(review.inspectionStatus?.name)
      ).length,
      inReview: reviews.filter((review) =>
        /in review/i.test(review.inspectionStatus?.name)
      ).length,
      mine: reviews.filter(assignedToMe).length,
    }),
    [reviews, currentUser]
  );

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setReviewPagination((current) => ({ ...current, page: 0 }));
  };

  const selectQuickFilter = (value) => {
    setQuickFilter(value);
    setReviewPagination((current) => ({ ...current, page: 0 }));
    // Keep the quick-filter selection and the visible "View" filter in sync.
    // Otherwise "All Reviews" can show a total for all records while the grid still
    // remains constrained to My Assigned Reviews.
    if (value === "all") updateFilter("view", "all");
    if (value === "mine" || value === "assigned") updateFilter("view", "mine");
  };

  const resetFilters = () => {
    setFilters({
      view: "all",
      status: "all",
      assigned: "all",
      due: "all",
    });
    setQuickFilter("all");
    setReviewPagination((current) => ({ ...current, page: 0 }));
  };

  const openReviewSummary = (reviewId, review) => {
    addInspectionActivity(reviewId, "Review summary opened.").catch(() => {});
    navigate(`/inspectionreview/${reviewId}`);
  };

  const openStartReview = async (reviewId, review) => {
    const workflowTask = activeWorkflowTaskForCurrentUser(review);
    if (!assignedToMe(review)) {
      ErrorNotify(
        "Only the user assigned to this review can start it. Change the assignment first."
      );
      return;
    }
    if (
      workflowTask?.taskType === "FinalSignOff" ||
      workflowTask?.taskType === "FindingResponse"
    ) {
      openReviewSummary(reviewId, review);
      return;
    }
    try {
      const response = await startInspectionReview(reviewId);
      if (!response?.status)
        throw new Error(response?.message || "Unable to start the review.");
      navigate(
        `/inspectionreview/${reviewId}/checklist${
          workflowTask?.id ? `?workflowTaskId=${workflowTask.id}` : ""
        }`,
        reviewListOrigin
      );
    } catch (error) {
      ErrorNotify(error.message || "Unable to start the review.");
    }
  };

  const openFindingResponse = async (review, workflowTask) => {
    try {
      const response = await getInspectionFindings(review.id);
      if (!response?.status)
        throw new Error(response?.message || "Unable to load the finding.");
      const finding = (response.data || []).find(
        (item) => Number(item.id) === Number(workflowTask.findingId)
      );
      if (!finding)
        throw new Error("The related finding is no longer available.");
      setWorkflowResponseDrawer({
        type: "finding",
        reviewId: review.id,
        task: workflowTask,
        item: finding,
      });
      setWorkflowResponseText(finding.managementResponse || "");
    } catch (error) {
      ErrorNotify(error.message || "Unable to open the finding.");
    }
  };

  const openQueryResponse = async (review, workflowTask) => {
    try {
      const response = await getInspectionChecklistQueries(review.id);
      if (!response?.status)
        throw new Error(response?.message || "Unable to load the query.");
      const query = (response.data || []).find(
        (item) =>
          Number(item.queryId || item.id) === Number(workflowTask.queryId)
      );
      if (!query) throw new Error("The related query is no longer available.");
      setWorkflowResponseDrawer({
        type: "query",
        reviewId: review.id,
        task: workflowTask,
        item: query,
      });
      setWorkflowResponseText(query.finalResponse || "");
    } catch (error) {
      ErrorNotify(error.message || "Unable to open the query.");
    }
  };

  const closeWorkflowResponse = () => {
    if (savingWorkflowResponse) return;
    setWorkflowResponseDrawer(null);
    setWorkflowResponseText("");
  };

  const saveWorkflowResponse = async () => {
    if (!workflowResponseDrawer || !workflowResponseText.trim()) {
      ErrorNotify("Enter a response before submitting it.");
      return;
    }
    setSavingWorkflowResponse(true);
    try {
      const isQuery = workflowResponseDrawer.type === "query";
      const response = isQuery
        ? await respondToInspectionChecklistQuery(
            workflowResponseDrawer.reviewId,
            workflowResponseDrawer.item.queryId ||
              workflowResponseDrawer.item.id,
            { response: workflowResponseText.trim() }
          )
        : await saveInspectionFindingManagementResponse(
            workflowResponseDrawer.reviewId,
            workflowResponseDrawer.item.id,
            { managementResponse: workflowResponseText.trim() }
          );
      if (!response?.status)
        throw new Error(
          response?.message ||
            `Unable to submit the ${isQuery ? "query" : "finding"} response.`
        );
      SuccessNotify(`${isQuery ? "Query" : "Finding"} response submitted.`);
      setWorkflowResponseDrawer(null);
      setWorkflowResponseText("");
      loadReviews();
    } catch (error) {
      ErrorNotify(
        error.message ||
          `Unable to submit the ${
            workflowResponseDrawer?.type === "query" ? "query" : "finding"
          } response.`
      );
    } finally {
      setSavingWorkflowResponse(false);
    }
  };

  const openWorkflowTask = async (review, workflowTask) => {
    if (!canOpenWorkflowTask(workflowTask)) {
      ErrorNotify(
        "Only the user assigned to an active workflow task can open it."
      );
      return;
    }
    if (workflowTask.taskType === "FindingResponse") {
      openFindingResponse(review, workflowTask);
      return;
    }
    if (["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(workflowTask.taskType)) {
      openQueryResponse(review, workflowTask);
      return;
    }
    if (workflowTask.taskType === "FinalSignOff") {
      navigate(
        `/inspectionreview/${review.id}?workflowTaskId=${workflowTask.id}`
      );
      return;
    }
    try {
      const response = await startInspectionReview(review.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to open this review task."
        );
      navigate(
        `/inspectionreview/${review.id}/checklist${
          workflowTask?.id ? `?workflowTaskId=${workflowTask.id}` : ""
        }`,
        reviewListOrigin
      );
    } catch (error) {
      ErrorNotify(error.message || "Unable to open this review task.");
    }
  };

  const openActivityHistory = async (review) => {
    setHistoryReview(review);
    setReviewActivities([]);
    setIsLoadingActivities(true);
    try {
      const response = await getInspectionActivities(review.id);
      if (!response?.status)
        throw new Error(response?.message || "Unable to load review activity.");
      setReviewActivities(response.data || []);
    } catch (error) {
      ErrorNotify(error.message || "Unable to load review activity.");
    } finally {
      setIsLoadingActivities(false);
    }
  };

  const saveReviewAssignment = async (assignment, change) => {
    const previous = reviewAssignmentDefaults(assignmentReview);
    const roleLabels = {
      owner: "Review Owner",
      primaryReviewer: "Primary Reviewer",
      approver: "Approver",
      observer: "Observer",
    };
    const activityHistory = Object.entries(roleLabels)
      .filter(([field]) => previous[field] !== assignment[field])
      .map(([field, label]) => ({
        id: `${field}-${Date.now()}`,
        note: `${label} changed from ${previous[field] || "Not assigned"} to ${
          assignment[field] || "Not assigned"
        }`,
        reason: change.reason,
        changedBy: "ABC Firm Admin",
        changedOn: new Date().toISOString(),
      }));
    if (
      JSON.stringify(previous.additionalReviewers) !==
      JSON.stringify(assignment.additionalReviewers)
    ) {
      activityHistory.push({
        id: `additional-${Date.now()}`,
        note: "Additional Reviewers updated",
        reason: change.reason,
        changedBy: "ABC Firm Admin",
        changedOn: new Date().toISOString(),
      });
    }
    try {
      let assignedToName = assignmentReview.assignedUser?.fullName;
      if (
        Number(assignment.assignedTo) !==
        Number(assignmentReview.assignedTo || assignmentReview.assignedUser?.id)
      ) {
        const response = await updateInspectionAssignment(
          assignmentReview.id,
          assignment.assignedTo
        );
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to update the review assignment."
          );
        assignedToName =
          response.data?.assignedToName ||
          users.find(
            (user) => Number(user.id) === Number(assignment.assignedTo)
          )?.fullName;
      }
      setReviews((current) =>
        current.map((review) =>
          review.id === assignmentReview.id
            ? {
                ...review,
                assignedTo: assignment.assignedTo,
                reviewAssignment: assignment,
                ownerUser: { ...review.ownerUser, fullName: assignment.owner },
                assignedUser: {
                  ...review.assignedUser,
                  id: assignment.assignedTo,
                  fullName: assignedToName || assignment.primaryReviewer,
                },
                assignmentChange: change,
                assignmentHistory: [
                  ...(review.assignmentHistory || []),
                  ...activityHistory,
                ],
              }
            : review
        )
      );
      setAssignmentReview(null);
      SuccessNotify("Review assignment updated successfully.");
    } catch (error) {
      ErrorNotify(error.message || "Unable to update the review assignment.");
      throw error;
    }
  };

  const reviewColumns = [
    {
      id: "treeToggle",
      header: "",
      accessorFn: () => "",
      size: 38,
      minSize: 38,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => {
        const item = row.original;
        if (item.__treeRowType === "review")
          return item.__hasPendingWorkflow ? (
            <button
              type="button"
              className="engagement-review-tree-toggle"
              onClick={(event) => {
                event.stopPropagation();
                toggleReviewExpansion(item.id);
              }}
              aria-label={`${
                expandedReviewIds.has(item.id) ? "Collapse" : "Expand"
              } pending work for ${item.inspectionNo || "review"}`}
              aria-expanded={expandedReviewIds.has(item.id)}
            >
              <i
                className={`bx bx-chevron-${
                  expandedReviewIds.has(item.id) ? "down" : "right"
                }`}
              />
            </button>
          ) : (
            <span />
          );
        return (
          <span
            className={`engagement-review-tree-branch engagement-review-tree-branch--${
              item.__treeRowType === "workflow-group" ? "group" : "task"
            }`}
            aria-hidden="true"
          >
            {item.__treeRowType === "workflow-group" ? (
              <i className="bx bx-chevron-down" />
            ) : (
              "•"
            )}
          </span>
        );
      },
    },
    {
      id: "reviewJob",
      header: "Review Job ID",
      accessorFn: (row) => row.inspectionNo || "-",
      size: 110,
      flex: 0.65,
      cell: ({ row, getValue }) => {
        const item = row.original;
        if (item.__treeRowType === "workflow-group")
          return (
            <span className="engagement-review-tree-group-label">
              <i className="bx bx-layer" />
              {item.__workflowGroup.label}
              <small>{item.__workflowGroup.tasks.length} pending</small>
            </span>
          );
        if (item.__treeRowType === "workflow-task")
          return (
            <span
              className="engagement-review-tree-task-label"
              title={
                item.__workflowTask.taskName ||
                workflowTaskLabel(item.__workflowTask)
              }
            >
              {workflowTaskLabel(item.__workflowTask)}
            </span>
          );
        return (
          <span className="engagement-review-job">
            <b>{getValue()}</b>
          </span>
        );
      },
    },
    {
      id: "inspectionCycle",
      header: "Inspection Cycle",
      accessorFn: (row) => row.inspectionCycleName || "—",
      size: 155,
      flex: 1,
      cell: ({ row, getValue }) => row.original.__treeRowType !== "review" ? null : <span>{getValue()}</span>,
    },
    {
      id: "engagement",
      header: "Engagement",
      accessorFn: row => row.engagementDetail?.name || "—",
      size: 190,
      flex: 1.2,
      cell: ({ row, getValue }) => row.original.__treeRowType !== "review" ? null : <span className="engagement-review-job"><b>{getValue()}</b><small>{row.original.engagementDetail?.engagementNo || "—"}</small></span>,
    },
    {
      id: "template",
      header: "Review Template\nRound",
      accessorFn: (row) =>
        row.reviewTemplate?.name || row.reviewTemplateVersion?.name || "-",
      size: 190,
      flex: 1.2,
      cell: ({ row, getValue }) => {
        const item = row.original;
        if (item.__treeRowType === "workflow-task")
          return (
            <span className="engagement-review-tree-role">
              {item.__workflowTask.assignedRole || "Assigned workflow task"}
            </span>
          );
        if (item.__treeRowType === "workflow-group") return null;
        return (
          <span className="engagement-review-template">
            <b>{getValue()}</b>
            <small>
              {item.reviewTemplateVersion?.name || "Round 1 of 1"} <i />
            </small>
          </span>
        );
      },
    },
    {
      id: "assignedTo",
      header: "Assigned To\nCurrent Action",
      accessorFn: (row) => displayNames(currentAssigneeNames(row)),
      size: 180,
      flex: 1.15,
      cell: ({ row }) => {
        const item = row.original;
        if (item.__treeRowType === "workflow-task") {
          const name = taskAssigneeName(item.__workflowTask);
          return (
            <div className="engagement-review-tree-assignee">
              <PeopleAvatarStack
                names={name === "Unassigned" ? [] : [name]}
                label={`Assigned to ${name}`}
              />
              <span>{name}</span>
            </div>
          );
        }
        if (item.__treeRowType === "workflow-group") return null;
        return <CurrentWorkflowAssignee review={item} />;
      },
    },
    {
      id: "dueDate",
      header: "Due Date\nCreated On",
      accessorFn: (row) => formatDateLong(row.dueDate),
      size: 155,
      flex: 1,
      cell: ({ row, getValue }) => {
        const item = row.original;
        if (item.__treeRowType !== "review")
          return <span className="engagement-review-tree-muted">—</span>;
        return (
          <span
            className={`engagement-review-date engagement-review-date--${dueState(
              item.dueDate
            )}`}
          >
            <b>{getValue()}</b>
            <small>Created: {formatDateLong(item.createdOn)}</small>
          </span>
        );
      },
    },
    {
      id: "statusProgress",
      header: "Status\nProgress",
      accessorFn: (row) => row.inspectionStatus?.name || "New",
      size: 175,
      flex: 1.1,
      align: "left",
      headerAlign: "left",
      cell: ({ row, getValue }) => {
        const item = row.original;
        if (item.__treeRowType === "workflow-task") {
          const status = item.__workflowTask.workflowStatus || "Pending";
          return (
            <span
              className={`engagement-review-tree-status engagement-review-tree-status--${status
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              {status}
            </span>
          );
        }
        if (item.__treeRowType === "workflow-group") return null;
        const progress = reviewProgress(item);
        return (
          <div className="engagement-review-status-progress">
            <span
              className={`engagement-review-status engagement-review-status--${reviewTone(
                getValue()
              )}`}
            >
              {getValue() === "Awaiting Approval" ? "Awaiting Sign-off" : getValue()}
            </span>
            <span className="engagement-review-progress">
              <b>{progress}%</b>
              <i>
                <em style={{ width: `${progress}%` }} />
              </i>
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      size: 220,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => {
        const item = row.original;
        if (item.__treeRowType === "review")
          return (
            <div className="engagement-review-row-actions">
              <button
                className="engagement-review-workflow-action"
                type="button"
                onClick={() => openReviewSummary(item.id, item)}
              >
                Review Summary
              </button>
            <InspectionActionMenu
              review={item}
              onReviewSummary={openReviewSummary}
              onStartReview={openStartReview}
              onReviewAssignment={setAssignmentReview}
              onActivityHistory={openActivityHistory}
              onCancelReview={setCancelNoticeReview}
              canManage={!isEngagementMember}
              canStart={assignedToMe(item)}
            />
            </div>
          );
        if (item.__treeRowType !== "workflow-task") return null;
        const task = item.__workflowTask;
        return canOpenWorkflowTask(task) ? (
          <button
            className="engagement-review-workflow-action"
            type="button"
            onClick={() => openWorkflowTask(item, task)}
          >
            <i
              className={`bx ${
                ["QueryResponse", "QueryResponseReview", "QueryResponseApproval"].includes(task.taskType)
                  ? "bx-message-rounded-dots"
                  : task.taskType === "FindingResponse"
                  ? "bx-flag"
                  : "bx-right-arrow-alt"
              }`}
            />{" "}
            {workflowTaskActionLabel(task)}
          </button>
        ) : (
          <span className="engagement-review-tree-muted">—</span>
        );
      },
    },
  ];

  return (
    <div className="page-content engagement-review-page piers-page">
      <header className="engagement-review-page__header piers-page-header">
        <div>
          <h1 className="piers-page-title">Inspection Reviews</h1>
          <p>Manage, assign, and monitor reviews across your firm.</p>
        </div>
        <div className="engagement-review-page__actions">
          <button
            type="button"
            className="engagement-review-refresh piers-refresh-button"
            onClick={loadReviews}
            title="Refresh engagement reviews"
          >
            <i className="bx bx-refresh" aria-hidden="true" /> Refresh
          </button>
        </div>
      </header>

      <SummaryCards
        columns={6}
        items={[
          [
            "all",
            "bx-clipboard",
            "blue",
            "Total Reviews",
            counts.total,
            "All engagement reviews",
          ],
          [
            "new",
            "bx-time-five",
            "orange",
            "New",
            counts.new,
            "Awaiting assignment",
          ],
          [
            "assigned",
            "bx-user",
            "purple",
            "Assigned",
            counts.mine,
            "Reviews in progress",
          ],
          [
            "completed",
            "bx-check-circle",
            "green",
            "Completed",
            counts.completed,
            "Closed reviews",
          ],
          [
            "overdue",
            "bx-error-circle",
            "red",
            "Overdue",
            counts.overdue,
            "Need attention",
          ],
          [
            "all",
            "bx-calendar",
            "blue",
            "Avg. Cycle Time",
            "7.2 days",
            "From last 30 days",
          ],
        ].map(([filter, icon, tone, label, value, note]) => ({
          icon,
          tone,
          label,
          value,
          note,
          onClick: () => selectQuickFilter(filter),
        }))}
        ariaLabel="Engagement review summary"
      />

      

      <div className="engagement-review-quick-filters">
        {[
          ["mine", "My Work", counts.mine, "bx-user"],
          ["all", "All Reviews", counts.total, "bx-grid-alt"],
          ["assigned", "Assigned to Me", counts.mine, "bx-user"],
          ["overdue", "Overdue", counts.overdue, null],
          ["queries", "Awaiting Queries", counts.awaitingQueries, null],
          ["review", "In Review", counts.inReview, null],
          ["completed", "Completed", counts.completed, null],
        ].map(([value, label, count, icon]) => (
          <button
            key={value}
            type="button"
            className={quickFilter === value ? "active" : ""}
            onClick={() => selectQuickFilter(value)}
          >
            {icon && <i className={`bx ${icon}`} />}
            {label}
            <b>{count}</b>
          </button>
        ))}
      </div>

      <section className="engagement-review-grid">
        <CustomDataGrid
 filterContent={<section
        className="engagement-review-filter-panel"
        aria-label="Engagement review filters"
      >
        <div className="engagement-review-filter-panel__fields">
          <label>
            <span>View</span>
            <select
              value={filters.view}
              onChange={(event) => {
                const value = event.target.value;
                updateFilter("view", value);
                setQuickFilter(value === "all" ? "all" : "mine");
              }}
            >
              <option value="all">All Reviews</option>
              <option value="mine">My Assigned Reviews</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              <option value="all">All Statuses</option>
              {[
                ...new Set(
                  reviews.map(
                    (review) => review.inspectionStatus?.name || "New"
                  )
                ),
              ].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Assigned To</span>
            <select
              value={filters.assigned}
              onChange={(event) => updateFilter("assigned", event.target.value)}
            >
              <option value="all">All Assignees</option>
              <option value="mine">Assigned to Me</option>
              {assigneeOptions.map((assignee) => (
                <option key={assignee} value={assignee}>
                  {assignee}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Due Date</span>
            <select
              value={filters.due}
              onChange={(event) => updateFilter("due", event.target.value)}
            >
              <option value="all">All Due Dates</option>
              <option value="overdue">Overdue</option>
              <option value="week">Due This Week</option>
              <option value="upcoming">Upcoming</option>
            </select>
          </label>
        </div>
        <div className="engagement-review-filter-panel__actions">
          <button type="button" onClick={resetFilters}>Clear</button>
        </div>
      </section>}

          data={reviewTreeRows}
          loading={gridLoading}
          emptyState={{
            title: "No reviews available yet",
            icon: "bx-check-shield",
            error: gridError,
            onRetry: loadReviews,
            description:
              "Reviews appear after an engagement is prepared and submitted. Contact your review manager if you are waiting for an assignment.",
            filtered:
              reviews.length > 0 ||
              !["mine", "all"].includes(quickFilter) ||
              Object.entries(filters).some(
                ([key, value]) => key !== "view" && value !== "all"
              ),
            onReset: () => {
              setQuickFilter("all");
              setFilters({
                view: "all",
                status: "all",
                assigned: "all",
                due: "all",
              });
            },
            helpHref: "/piers/getting-started",
          }}
          columns={reviewColumns}
          pagination={false}
          paginationModel={{
            page: 0,
            pageSize: Math.max(10, reviewTreeRows.length),
          }}
          onPaginationModelChange={() => {}}
          sortingMode="server"
          sortModel={reviewSortModel}
          onSortModelChange={setReviewSortModel}
          footerPagination={{
            allowAll: true,
            model: reviewPagination,
            rowCount: sortedVisibleReviews.length,
            onChange: setReviewPagination,
          }}
          onRefresh={loadReviews}
          fileName="engagement-reviews"
          recordLabel="engagement reviews"
          showToolbar={false}
          initialPageSize={10}
          rowHeight={64}
          getRowHeight={(params) =>
            params.model.__original?.__treeRowType === "workflow-group"
              ? 32
              : params.model.__original?.__treeRowType === "workflow-task"
              ? 42
              : 64
          }
          getRowClassName={(params) =>
            `engagement-review-tree-row engagement-review-tree-row--${
              params.row.__original?.__treeRowType || "review"
            }`
          }
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e4e8f0",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".2px",
              lineHeight: 1.35,
              whiteSpace: "pre-line",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              borderBottomColor: "#edf0f5",
              color: "#273957",
              fontSize: 13,
            },
            "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { borderTopColor: "#e7ebf2", fontSize: 14 },
          }}
        />
      </section>

      <Offcanvas
        backdrop="static"
        className="engagement-review-assignment-drawer"
        direction="end"
        isOpen={Boolean(assignmentReview)}
        toggle={() => setAssignmentReview(null)}
      >
        <OffcanvasBody>
          {assignmentReview && (
            <ReviewAssignmentDrawer
              review={assignmentReview}
              users={users}
              onClose={() => setAssignmentReview(null)}
              onSave={saveReviewAssignment}
            />
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Offcanvas
        className="engagement-review-activity-drawer"
        direction="end"
        isOpen={Boolean(historyReview)}
        toggle={() => setHistoryReview(null)}
      >
        <OffcanvasBody className="review-activity-history">
          <header>
            <div>
              <h2>Activity History</h2>
              <p>{historyReview?.inspectionNo || "Engagement review"}</p>
            </div>
            <button
              type="button"
              onClick={() => setHistoryReview(null)}
              aria-label="Close activity history"
            >
              <i className="bx bx-x" />
            </button>
          </header>
          {isLoadingActivities ? (
            <div className="review-activity-history__loading">
              <span className="spinner-border spinner-border-sm" /> Loading
              activity…
            </div>
          ) : reviewActivities.length ? (
            <div className="review-activity-history__list">
              {reviewActivities.map((activity) => (
                <article key={activity.id}>
                  <i className="bx bx-history" />
                  <div>
                    <b>{activity.activityNote}</b>
                    <span>{activity.userProfile?.fullName || "User"}</span>
                    <small>{formatDate(activity.activityDate, true)}</small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="review-activity-history__empty">
              <i className="bx bx-history" />
              <b>No activity recorded yet</b>
              <p>
                Review actions will appear here as users work on this review.
              </p>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal
        centered
        isOpen={Boolean(cancelNoticeReview)}
        toggle={() => setCancelNoticeReview(null)}
      >
        <ModalBody className="review-assignment-confirm">
          <h3>Cancel Review</h3>
          <p>Cancel review is not implemented yet.</p>
          <div>
            <button
              className="is-primary"
              type="button"
              onClick={() => setCancelNoticeReview(null)}
            >
              OK
            </button>
          </div>
        </ModalBody>
      </Modal>

      <WorkflowResponseDrawer
        onReviewPosted={loadReviews}
        response={workflowResponseDrawer}
        value={workflowResponseText}
        onChange={setWorkflowResponseText}
        onClose={closeWorkflowResponse}
        onSubmit={saveWorkflowResponse}
        saving={savingWorkflowResponse}
      />
    </div>
  );
};

export default InspectionReviewList;
